import { Donation } from '@shared/models/Donation';
import { DonationListParams, PaginatedResponse, CreateDonationRequest, CreateDonationResponse } from '../types/api';
import { get, post } from './api';
import { logDebug, logError, logInfo } from '../utils/errorHandler';
import * as WechatAuthService from './WechatAuthService';

/**
 * 捐赠服务
 */
export class DonationService {
  /**
   * 获取捐赠列表（公开接口，用于H5，只返回已支付的捐赠）
   */
  static async getDonations(params?: DonationListParams): Promise<PaginatedResponse<Donation>> {
    return get<PaginatedResponse<Donation>>('/public/donations', params);
  }

  /**
   * 获取捐赠详情（公开接口，H5用）
   * 需要 openId 参数验证用户身份
   * @param organizationType 机构类型（可选），用于计算累计金额时正确过滤
   * @param organizationId 机构ID（可选），用于计算累计金额时正确过滤
   */
  static async getDonationById(id: string, organizationType?: string, organizationId?: string): Promise<Donation> {
    const openId = WechatAuthService.getOpenId();
    if (!openId) {
      throw new Error('未获取到微信授权信息，无法获取捐赠详情');
    }
    const params: any = { openId };
    if (organizationType) {
      params.organizationType = organizationType;
    }
    if (organizationId) {
      params.organizationId = organizationId;
    }
    return get<Donation>(`/public/donations/${id}`, params);
  }

  /**
   * 创建捐赠订单
   * 如果是单次捐赠且提供了 openId，会返回支付参数
   */
  static async createDonation(data: CreateDonationRequest): Promise<CreateDonationResponse> {
    logInfo('[DonationService] createDonation called', {
      projectId: data.projectId,
      amount: data.amount,
      donationType: data.donationType,
      hasOpenId: !!data.donorOpenId,
      donorName: data.donorName,
      donorPhone: data.donorPhone
    });

    try {
      logInfo('[DonationService] Calling post /donations', {
        projectId: data.projectId,
        amount: data.amount,
        donationType: data.donationType
      });
      const result = await post<CreateDonationResponse>('/donations', data);
      logInfo('[DonationService] post /donations response received', {
        hasResult: !!result,
        id: result?.id,
        status: result?.status,
        hasPayment: !!result?.payment,
        donationOrderNo: result?.donationOrderNo,
        paymentKeys: result?.payment ? Object.keys(result.payment) : []
      });
      logInfo('[DonationService] Donation created', {
        id: result.id,
        status: result.status,
        hasPayment: !!result.payment,
        donationOrderNo: result.donationOrderNo
      });
      return result;
    } catch (error: any) {
      logError('[DonationService] createDonation failed', error);
      
      // 如果错误响应中包含订单记录（支付失败但订单已创建的情况）
      if (error?.responseData?.donation) {
        logInfo('[DonationService] Donation record found in error response', {
          donationId: error.responseData.donation.id,
          status: error.responseData.donation.status,
          donationOrderNo: error.responseData.donation.donationOrderNo
        });
        // 将订单记录附加到错误对象上，方便调用方访问
        error.donation = error.responseData.donation;
      }
      
      throw error;
    }
  }

  /**
   * 取消月捐
   */
  static async cancelMonthlyDonation(id: string): Promise<Donation> {
    // 这里应该调用实际的 API，暂时返回模拟数据
    return get<Donation>(`/donations/${id}`);
  }
}

